import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Home, Loader2, User, Mail, Phone, PoundSterling, Building2 } from "lucide-react";
import { API } from "@/App";

export const ApplicantRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    min_budget: "",
    max_budget: "",
    min_bedrooms: "",
    max_bedrooms: "",
    finance_status: "unknown",
    chain_status: "unknown",
    consent_emails: false,
    consent_sms: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        min_budget: formData.min_budget ? parseFloat(formData.min_budget) : null,
        max_budget: formData.max_budget ? parseFloat(formData.max_budget) : null,
        min_bedrooms: formData.min_bedrooms ? parseInt(formData.min_bedrooms) : null,
        max_bedrooms: formData.max_bedrooms ? parseInt(formData.max_bedrooms) : null,
        finance_status: formData.finance_status,
        chain_status: formData.chain_status,
        consent_emails: formData.consent_emails,
        consent_sms: formData.consent_sms
      };
      
      const response = await axios.post(`${API}/buyer-portal/register`, payload);
      
      if (response.data.success) {
        // Store token and applicant data
        localStorage.setItem("buyer_token", response.data.token);
        localStorage.setItem("buyer_applicant", JSON.stringify(response.data.applicant));
        
        toast.success("Registration successful! Welcome to Goodacres.");
        navigate("/buyer/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Home className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Register as a Buyer</CardTitle>
          <CardDescription>
            Create an account to search properties, book viewings, and make offers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                Personal Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange("confirmPassword", e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Property Preferences */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Property Preferences
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min_budget">Min Budget (£)</Label>
                  <Input
                    id="min_budget"
                    type="number"
                    placeholder="e.g. 200000"
                    value={formData.min_budget}
                    onChange={(e) => handleChange("min_budget", e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="max_budget">Max Budget (£)</Label>
                  <Input
                    id="max_budget"
                    type="number"
                    placeholder="e.g. 400000"
                    value={formData.max_budget}
                    onChange={(e) => handleChange("max_budget", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min_bedrooms">Min Bedrooms</Label>
                  <Select value={formData.min_bedrooms} onValueChange={(v) => handleChange("min_bedrooms", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1+</SelectItem>
                      <SelectItem value="2">2+</SelectItem>
                      <SelectItem value="3">3+</SelectItem>
                      <SelectItem value="4">4+</SelectItem>
                      <SelectItem value="5">5+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="max_bedrooms">Max Bedrooms</Label>
                  <Select value={formData.max_bedrooms} onValueChange={(v) => handleChange("max_bedrooms", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5">5+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Buying Position */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <PoundSterling className="w-4 h-4" />
                Your Buying Position
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Finance Status</Label>
                  <Select value={formData.finance_status} onValueChange={(v) => handleChange("finance_status", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Not sure yet</SelectItem>
                      <SelectItem value="cash">Cash Buyer</SelectItem>
                      <SelectItem value="mortgage_aip">Mortgage AIP in place</SelectItem>
                      <SelectItem value="mortgage_pending">Mortgage pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chain Status</Label>
                  <Select value={formData.chain_status} onValueChange={(v) => handleChange("chain_status", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Not sure yet</SelectItem>
                      <SelectItem value="no_chain">No chain (first time buyer / renting)</SelectItem>
                      <SelectItem value="related_sale">Have a property to sell</SelectItem>
                      <SelectItem value="full_chain">In a chain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Marketing Consent */}
            <div className="space-y-3 bg-slate-50 p-4 rounded-lg">
              <h3 className="font-semibold text-slate-700">Keep me updated</h3>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="consent_emails"
                  checked={formData.consent_emails}
                  onCheckedChange={(checked) => handleChange("consent_emails", checked)}
                />
                <Label htmlFor="consent_emails" className="text-sm">
                  Email me about new properties matching my criteria
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="consent_sms"
                  checked={formData.consent_sms}
                  onCheckedChange={(checked) => handleChange("consent_sms", checked)}
                />
                <Label htmlFor="consent_sms" className="text-sm">
                  Send me SMS alerts for viewings and updates
                </Label>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
            
            <p className="text-center text-sm text-slate-600">
              Already have an account?{" "}
              <Link to="/buyer/login" className="text-blue-600 hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApplicantRegister;
